/**
 * PremiumTriggerCard — Carte de conversion Premium contextuelle (P2 B1).
 *
 * S'affiche dans le cockpit réseau uniquement si :
 * - l'utilisateur n'a pas encore Premium ;
 * - un des 3 signaux backend s'est déclenché (preuves publiées, taux de
 *   réponse aux invitations d'avis, ou ancienneté + complétude) ;
 * - l'utilisateur n'a pas dismissé (30 j de cooldown côté backend).
 *
 * Design : pas de rouge, pas de faux compte à rebours. Message centré
 * sur ce que l'utilisateur a déjà accompli.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, X, Loader2 } from 'lucide-react';
import api from '../../lib/api';

export default function PremiumTriggerCard({ onDismiss }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissing, setDismissing] = useState(false);
    const [hidden, setHidden] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get('/reseau/premium/trigger');
                if (!cancelled) setData(r.data);
            } catch (e) { /* silencieux */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleDismiss = async () => {
        setDismissing(true);
        try { await api.post('/reseau/premium/trigger/dismiss', { reason: 'user_dismiss' }); }
        catch (e) { /* ignore */ }
        setHidden(true);
        setDismissing(false);
        if (onDismiss) onDismiss();
    };

    const handleCTA = () => {
        try { api.post('/reseau/analytics/track', { event_type: 'premium_trigger_cta_clicked', context: { reason: data?.reason_code } }); }
        catch (e) { /* ignore */ }
        navigate('/reseau/parametres');
    };

    if (loading || !data || !data.should_show || hidden) return null;

    return (
        <section
            data-testid="premium-trigger-card"
            className="rounded-2xl p-5 relative overflow-hidden"
            style={{
                background: 'linear-gradient(135deg, rgba(104,23,147,0.10) 0%, rgba(255,145,77,0.10) 100%)',
                border: '1px solid rgba(104,23,147,0.25)',
            }}
        >
            <button
                type="button"
                onClick={handleDismiss}
                disabled={dismissing}
                aria-label="Masquer cette proposition"
                data-testid="premium-trigger-dismiss"
                className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-black/5 transition"
                style={{ color: 'var(--reseau-ink-muted)' }}
            >
                {dismissing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            </button>

            <div className="flex items-start gap-3 mb-3 pr-8">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                     style={{ background: 'var(--reseau-violet, #681793)', color: '#fff' }}>
                    <Sparkles className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wider"
                       style={{ color: 'var(--reseau-violet)' }}
                       data-testid="premium-trigger-reason">
                        {data.reason_code === 'proofs_engaged' && 'Preuves qui font effet'}
                        {data.reason_code === 'reviews_traction' && 'Traction avis vendeurs'}
                        {data.reason_code === 'mature_profile' && 'Profil arrivé à maturité'}
                    </p>
                    <h3 className="text-base font-bold leading-tight mt-0.5"
                        style={{ color: 'var(--reseau-ink)' }}>
                        Vous êtes prêt pour Homly Réseau Premium
                    </h3>
                    <p className="text-sm mt-1"
                       style={{ color: 'var(--reseau-ink-muted)' }}>
                        {data.primary_message}
                    </p>
                </div>
            </div>

            {data.triggers && data.triggers.length > 1 && (
                <ul className="mt-3 space-y-1.5" data-testid="premium-trigger-signals">
                    {data.triggers.slice(1).map((t) => (
                        <li key={t.code}
                            className="text-xs flex items-start gap-2"
                            style={{ color: 'var(--reseau-ink-muted)' }}
                            data-testid={`premium-trigger-signal-${t.code}`}>
                            <span className="mt-1 w-1 h-1 rounded-full flex-shrink-0"
                                  style={{ background: 'var(--reseau-violet)' }} />
                            <span>{t.label}</span>
                        </li>
                    ))}
                </ul>
            )}

            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                    <strong style={{ color: 'var(--reseau-ink)' }}>19 € HT/mois</strong> · résiliable
                    à tout moment · CV PDF, statistiques avancées, assistant IA de bio.
                </p>
                <button
                    type="button"
                    onClick={handleCTA}
                    data-testid="premium-trigger-cta"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ background: 'var(--reseau-violet, #681793)', color: '#fff' }}
                >
                    Découvrir Premium
                    <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </section>
    );
}
